import { useQueryClient } from '@tanstack/react-query'
import { useEffect, useState } from 'react'

import {
  isNotificationWire,
  notificationFromWire,
} from '@/features/notifications/api/notificationWire'
import { queryKeys } from '@/lib/query'
import { USE_MOCKS } from '@/mocks/fixtures'
import type { Notification, NotificationFeed } from '@/types/domain'

/* The live bell (NFR-2).
 *
 * The transport is Server-Sent Events rather than a WebSocket, because the
 * traffic is one-directional: events travel server→client, and marking read is
 * the POST the feed already has. EventSource also reconnects by itself, which
 * is the behaviour that matters most here — the people using this product are
 * on hotel wifi and phone tethering, and a hand-rolled socket would need its own
 * backoff, its own heartbeat and its own reconnect bookkeeping to match what the
 * browser already does.
 *
 * The stream is an ACCELERATOR, never the source of truth. Everything it
 * delivers is already committed and already readable from GET /notifications,
 * so a dropped event costs latency and nothing else. That is what makes the
 * reconnect strategy below safe: on every reconnect we refetch, and the feed
 * corrects itself.
 */

/* The event shape the server sends on the `notification` channel is the same
 * `NotificationWire` GET /api/notifications serves — one outbox row, one
 * spelling — so the topic→kind conversion is shared with the feed query rather
 * than kept here. See notificationWire.ts for why it moved. */

/** Merges one streamed event into a cached feed.
 *
 *  Exported for its test, and worth testing on its own: it is the one piece of
 *  this file that is pure logic rather than browser plumbing.
 *
 *  Deduplicated by id, and NOT append-only. The same event can legitimately
 *  arrive twice — a reconnect that overlaps a refetch, two tabs sharing a cache
 *  — and a feed that appended blindly would show a case's assignment twice and
 *  count it twice in the badge. */
export function mergeStreamedNotification(
  feed: NotificationFeed | undefined,
  incoming: Notification,
): NotificationFeed | undefined {
  // Nothing cached yet means nothing has read the feed. Seeding a single-event
  // feed here would render a panel holding one row and hide everything else
  // until the next fetch; leaving it undefined lets the query populate it
  // properly.
  if (!feed) return feed

  const existing = feed.items.findIndex((item) => item.id === incoming.id)

  const items =
    existing === -1
      ? [incoming, ...feed.items]
      : feed.items.map((item, index) => (index === existing ? incoming : item))

  // Recomputed from the items rather than incremented, so a duplicate delivery
  // cannot drift the badge away from the list it sits above.
  return { items, unreadCount: items.filter((item) => !item.read).length }
}

/** Decodes one SSE frame, or null for anything that is not a bell event.
 *
 *  Exported for its test, and separate from the listener for the reason
 *  mergeStreamedNotification is: this is the pure half of the stream, and the
 *  half that has to be right about hostile input.
 *
 *  TWO refusals, not one. Unparseable JSON is a server bug and dropping it is
 *  right — the feed is durable, so the event is still in the next fetch. A frame
 *  that PARSES but is not a notification is the one that used to get through:
 *  `{"foo":1}` is valid JSON, and a cast to `NotificationWire` put a row with no
 *  id and no href into the cache, where the drawer rendered `<Link to={undefined}>`
 *  and react-router threw. One malformed frame, whole panel gone. */
export function parseStreamedNotification(data: string): Notification | null {
  let raw: unknown
  try {
    raw = JSON.parse(data)
  } catch {
    return null
  }

  if (!isNotificationWire(raw)) return null
  return notificationFromWire(raw)
}

/** Connection state, for the panel to show that live updates are or are not
 *  flowing. */
export type StreamStatus = 'connecting' | 'open' | 'closed'

/* Subscribes to the live feed for as long as the component is mounted.
 *
 * Mounted once, in the app shell: the bell is persistent chrome, and one
 * connection per app is the point. Calling this from two components would open
 * two streams and deliver every event twice — the merge above would survive it,
 * but the connections would be real.
 *
 * Returns the connection status, which the panel uses to say whether it is live.
 */
export function useNotificationStream(enabled = true): StreamStatus {
  const queryClient = useQueryClient()
  const [status, setStatus] = useState<StreamStatus>('connecting')

  // Whether a stream is possible at all. Mocks have no server to stream from,
  // and EventSource against an environment that lacks it (jsdom) or a dev server
  // that does not implement the route would retry forever in the console.
  //
  // Derived during render rather than set from inside the effect: a `setStatus`
  // in the effect body would render once as 'connecting' and then again as
  // 'closed', for a connection that was never going to be attempted.
  const canStream = enabled && !USE_MOCKS && typeof EventSource !== 'undefined'

  useEffect(() => {
    if (!canStream) return

    // Same-origin, and `withCredentials` for the session cookie: the session is
    // an httpOnly cookie (AD-21), so there is no token to put in a header — and
    // EventSource cannot set headers anyway, which is one more reason this
    // transport suits this product's auth.
    const source = new EventSource('/api/notifications/stream', {
      withCredentials: true,
    })

    source.onopen = () => setStatus('open')

    source.addEventListener('notification', (event) => {
      const incoming = parseStreamedNotification((event as MessageEvent<string>).data)
      if (!incoming) return

      // The in-flight read is cancelled BEFORE the merge, which is the same
      // dance useMarkNotificationsRead does around its optimistic update and
      // for the same reason. Without it, a `GET /notifications` that was
      // already on the wire when the event arrived resolves afterwards and
      // overwrites the cache with its own pre-event snapshot — the row and the
      // badge bump both disappear, and stay gone until the next invalidation,
      // which on a quiet connection is the next reconnect. It reads as "the
      // bell missed one", occasionally, under a race nobody can reproduce.
      //
      // Cancelling reverts that query to its previous state rather than
      // erroring, and the stream's own contract makes the loss harmless: the
      // event we are merging is already committed server-side, so the cache we
      // write is not behind the response we threw away.
      void queryClient
        .cancelQueries({ queryKey: queryKeys.notifications.feed() })
        .then(() =>
          queryClient.setQueryData<NotificationFeed>(queryKeys.notifications.feed(), (feed) =>
            mergeStreamedNotification(feed, incoming),
          ),
        )
    })

    source.onerror = () => {
      // EventSource reconnects on its own; this fires on each drop. We do NOT
      // close it here — closing would end the browser's retry and leave the bell
      // permanently stale.
      //
      // The refetch is what makes a reconnection correct rather than merely
      // reconnected: events published while the connection was down were never
      // delivered, and only a read of the feed can recover them. It is
      // invalidation rather than an append, so the server's feed wins.
      setStatus('connecting')
      void queryClient.invalidateQueries({ queryKey: queryKeys.notifications.all })
    }

    return () => {
      source.close()
      setStatus('closed')
    }
  }, [canStream, queryClient])

  return canStream ? status : 'closed'
}
